import { balance as defaultBalance, type Balance } from '../balance';
import type { CallView, Coach, WindowView } from '../game';
import { playById } from '../playbook';
import { createRng } from '../rng';
import { applyWindow, hasWindow } from '../subs';
import type {
  DefenseDecision,
  DefenseSlot,
  Game,
  OffenseDecision,
  OffenseSlot,
  SubWindow,
  TeamId,
} from '../types';
import { callPlay } from './call';
import { aiContext, lineups, type Difficulty } from './context';
import { conversionCall, fourthDownCall } from './fourth';
import { planSpends } from './spend';
import { planWindow } from './subs';

/**
 * The AI coach (slice 7): one Coach for all three levels (balance.json `coach.difficulty`), making
 * every decision through the same SnapDecision the player and PvP use. It reads only the view it is
 * handed, which shows the other side's personnel and never its call or spends, and it draws only
 * from its own rng, so a game replays from its seed and the decisions this coach made.
 *
 * Per call: the offense plans its sub window first (and hands it back from `subs`, so the defense
 * sees the new personnel), then the play on the board, then spends for the chart after its window,
 * then 4th down or the try. The defense's window comes from `subs`; its call sees the result.
 * Tempo is always normal: the AI does not run hurry-up in v1.
 */
export function aiCoach(seed: number, difficulty: Difficulty, b: Balance = defaultBalance): Coach {
  const ctx = aiContext(difficulty, createRng(seed), b);
  /** The offense's window planned in its call, handed back when the engine asks for it. */
  const planned = new Map<number, SubWindow<string>>();

  const after = (game: Readonly<Game>, side: 'offense' | 'defense', w: SubWindow<string>): Game => {
    if (!hasWindow(w)) return game as Game;
    const id = side === 'offense' ? game.possession : game.possession === 'A' ? 'B' : 'A';
    const team = applyWindow(game.teams[id], side, w, b).team;
    return { ...game, teams: { ...game.teams, [id]: team } };
  };

  return {
    offense(view: CallView): OffenseDecision {
      const window = planWindow(view.game, 'offense', ctx);
      planned.set(view.call, window);
      const game = after(view.game, 'offense', window);
      const sides = lineups(game, 'offense');
      const playId = callPlay(view, 'offense', ctx, game, sides);
      const play = playById(view.playbook.offense, playId);
      const twoPoint = view.kind === 'conversion';
      const { ours, theirs } = sides;
      const decision: OffenseDecision = {
        playId,
        moves: planSpends(play, 'offense', ours, theirs, game, twoPoint, ctx) as Record<
          OffenseSlot,
          string | null
        >,
        tempo: 'normal',
      };
      if (view.kind === 'conversion') decision.conversion = conversionCall(game, ctx);
      else if (game.down === b.football.downs) decision.fourthDown = fourthDownCall(game, ctx);
      return decision;
    },
    defense(view: CallView): DefenseDecision {
      const sides = lineups(view.game, 'defense');
      const playId = callPlay(view, 'defense', ctx, view.game, sides);
      const play = playById(view.playbook.defense, playId);
      const { ours, theirs } = sides;
      return {
        playId,
        moves: planSpends(
          play,
          'defense',
          ours,
          theirs,
          view.game,
          view.kind === 'conversion',
          ctx,
        ) as Record<DefenseSlot, string | null>,
      };
    },
    subs(view: WindowView): SubWindow<string> {
      if (view.side === 'offense') {
        const w = planned.get(view.call) ?? {};
        planned.delete(view.call);
        return w;
      }
      return planWindow(view.game, 'defense', ctx);
    },
  };
}

/** AI coaches for both seats of game `seed`, each level with its own rng derived from the seed. */
export function aiCoaches(
  seed: number,
  levels: Record<TeamId, Difficulty>,
  b: Balance = defaultBalance,
): Record<TeamId, Coach> {
  return {
    A: aiCoach(4 * seed + 3, levels.A, b),
    B: aiCoach(4 * seed + 4, levels.B, b),
  };
}
